import { useCallback, useEffect, useState } from "react";

// Not in any standard DOM lib yet (Chromium-only, still non-standard) —
// declared locally rather than pulling in a types package for one event.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * Captures the browser's own install-eligibility signal rather than trying
 * to second-guess it (the "visited more than once" part of the AC is each
 * browser's own heuristic — Chromium fires `beforeinstallprompt` once it
 * decides the engagement bar is met, which this hook can't override, only
 * listen for). `promptInstall()` can only be called from within a real user
 * gesture (a click handler), per the browser's own requirement.
 */
export default function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredEvent(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    setDeferredEvent(null);
  }, [deferredEvent]);

  return { isInstallable: !!deferredEvent && !installed, promptInstall };
}
